import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { channelWa } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Text, Button } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../../i18n/I18nProvider';
import { formatDateTime } from '../../../lib/format';

const MUTED = 'var(--rr-color-text-muted)';
const BADGE: Record<channelWa.MetaStatusState, 'healthy' | 'down' | 'pending' | 'inactive'> = {
  ok: 'healthy', attention: 'down', unknown: 'pending', later: 'inactive'
};

/*
 * Meta status (3 October 2026): what Meta has registered and verified for the
 * platform, asked live, because the owner "might be Meta verified but not
 * aware". Each line shows our state, Meta's own word beside it, and what to
 * do. A grey "unknown" is never a green one: Meta not answering is not a yes.
 * What Meta cannot be asked (Tech Provider approval) says so and shows the
 * owner's own confirmation from Settings, Integrations, WhatsApp. The same
 * check runs every six hours, and a line that changes is emailed to the owner.
 */
export default function MetaStatusPage() {
  const t = useT();
  const q = useQuery({
    queryKey: ['admin', 'meta-status'],
    queryFn: () => channelWa.metaStatus(),
    staleTime: 60_000,
    refetchOnWindowFocus: false
  });
  const status = q.data?.data;

  async function checkAgain() {
    await channelWa.metaStatus({ refresh: true });
    await q.refetch();
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('metaStatus.title')}
        description={t('metaStatus.description')}
        actions={<Button size="sm" variant="outline" loading={q.isFetching} onClick={() => void checkAgain()}>{t('metaStatus.checkAgain')}</Button>}
      />
      {q.error && <Alert color="error" role="alert" title={t('metaStatus.loadError')}><Text role="bodySmall">{(q.error as Error).message}</Text></Alert>}
      {status && (
        <>
          <Alert color={status.attention > 0 ? 'warning' : 'success'} data-testid="meta-status-summary">
            <Text role="bodySmall">
              {status.attention === 1 ? t('metaStatus.summary.attentionOne') : status.attention > 0 ? t('metaStatus.summary.attention', { count: status.attention }) : t('metaStatus.summary.ok')}
              {' '}{t('metaStatus.checkedAt', { when: formatDateTime(status.checked_at) })}
            </Text>
          </Alert>
          <Card withBorder data-testid="meta-status-coexistence">
            <Stack gap="xs">
              <Group gap="xs">
                <Text role="label">{t('metaStatus.coexistence.title')}</Text>
                <StatusBadge status={status.coexistence.enabled ? 'healthy' : 'inactive'}>
                  {t(status.coexistence.enabled ? 'metaStatus.coexistence.on' : 'metaStatus.coexistence.off')}
                </StatusBadge>
              </Group>
              <Text role="bodySmall" c={MUTED}>
                {status.coexistence.ready ? t('metaStatus.coexistence.ready') : t('metaStatus.coexistence.notReady')}
              </Text>
              {!status.coexistence.ready && (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {status.coexistence.missing.map((m) => (
                    <li key={m}><Text role="bodySmall">{m.startsWith('webhook.') ? t('metaStatus.coexistence.missing.webhook', { field: m.slice(8) }) : t(`metaStatus.coexistence.missing.${m}`)}</Text></li>
                  ))}
                </ul>
              )}
              <Text role="caption" c={MUTED}>
                {t('metaStatus.coexistence.switchHint')} <Link to="/settings/integrations">{t('metaStatus.coexistence.switchLink')}</Link>
              </Text>
            </Stack>
          </Card>
          {status.sections.map((section) => (
            <Card key={section.code} withBorder data-testid={`meta-status-${section.code}`}>
              <Stack gap="sm">
                <Text role="label">{section.title}</Text>
                {section.items.map((item) => (
                  <Group key={item.code} justify="space-between" align="flex-start" wrap="nowrap" data-testid="meta-status-item" data-state={item.state}>
                    <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                      <Text role="bodySmall" fw={600}>{item.label}</Text>
                      <Text role="caption" c={MUTED}>{item.detail}</Text>
                    </Stack>
                    <Stack gap={2} align="flex-end" style={{ flexShrink: 0 }}>
                      <StatusBadge status={BADGE[item.state]}>{t(`metaStatus.state.${item.state}`)}</StatusBadge>
                      {item.value && <Text role="caption" c={MUTED}>{item.value}</Text>}
                    </Stack>
                  </Group>
                ))}
              </Stack>
            </Card>
          ))}
        </>
      )}
    </Stack>
  );
}
